import { ApiResponse, ServiceError, LoginApiResponse, DataApiResponse } from '@/types/api';

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean;
}

export class ApiService {
  private static instance: ApiService;
  private baseUrl: string;
  private authToken: string | null = null;

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private constructor() {
    // Check if we should use proxy or direct API
    const useDirectAPI = import.meta.env.VITE_USE_DIRECT_API === 'true' || 
                        import.meta.env.REACT_APP_USE_DIRECT_API === 'true' || 
                        !import.meta.env.DEV;

    if (useDirectAPI) {
      // Use direct API URL (production or forced)
      this.baseUrl = import.meta.env.VITE_API_BASE_URL || 
                    import.meta.env.REACT_APP_API_BASE_URL || 
                    'https://zn6zngzwsa.execute-api.sa-east-1.amazonaws.com/prod/api';
    } else {
      // Use proxy (development only)
      this.baseUrl = '/api';
    }
    
    console.log('API Service initialized with baseUrl:', this.baseUrl);
    console.log('Environment:', { 
      DEV: import.meta.env.DEV, 
      useDirectAPI,
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      REACT_APP_API_BASE_URL: import.meta.env.REACT_APP_API_BASE_URL
    });
    
    // Load token from localStorage if available
    this.authToken = localStorage.getItem('urubu_auth_token');
  }

  // Set authentication token
  setAuthToken(token: string | null): void {
    this.authToken = token;
    if (token) {
      localStorage.setItem('urubu_auth_token', token);
    } else {
      localStorage.removeItem('urubu_auth_token');
    }
  }

  // Get authentication token
  getAuthToken(): string | null {
    return this.authToken;
  }

  // Make HTTP request
  private async makeRequest<T>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
    const {
      method = 'GET',
      headers: customHeaders = {},
      body,
      requiresAuth = false
    } = config;

    try {
      const url = `${this.baseUrl}${endpoint}`;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...customHeaders
      };

      // Add authorization header if required and token is available
      if (requiresAuth && this.authToken) {
        headers.Authorization = `Bearer ${this.authToken}`;
      }

      const requestConfig: RequestInit = {
        method,
        headers,
        mode: 'cors',
        credentials: 'omit',
        ...(body && { body: JSON.stringify(body) })
      };

      console.log(`Making ${method} request to ${url}`, { headers: Object.keys(headers), body });

      const response = await fetch(url, requestConfig);
      const data = await response.json();

      console.log(`Response from ${endpoint}:`, { status: response.status, data });

      // Return the API response as-is since our backend already formats it correctly
      return data as ApiResponse<T>;
      
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      return this.createErrorResponse(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Authentication endpoints
  async login(username: string, password: string): Promise<ApiResponse<LoginApiResponse>> {
    return this.makeRequest<LoginApiResponse>('/auth/login', {
      method: 'POST',
      body: { username, password }
    });
  }

  async validateToken(): Promise<ApiResponse<{ valid: boolean; user?: any }>> {
    return this.makeRequest('/auth/validate', {
      requiresAuth: true
    });
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.makeRequest('/auth/logout', {
      method: 'POST',
      requiresAuth: true
    });
    
    // Clear token on logout
    this.setAuthToken(null);
    return response;
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<LoginApiResponse>> {
    return this.makeRequest<LoginApiResponse>('/auth/refresh', {
      method: 'POST',
      body: { refreshToken }
    });
  }

  // Data endpoints  
  async getCurrentData(): Promise<ApiResponse<DataApiResponse>> {
    return this.makeRequest<DataApiResponse>('/data/current', {
      requiresAuth: true
    });
  }

  // Public endpoint for landing page (no auth required)
  async getPublicData(): Promise<ApiResponse<DataApiResponse>> {
    return this.makeRequest<DataApiResponse>('/data/current', {
      requiresAuth: false
    });
  }

  async updateData(data: any): Promise<ApiResponse<DataApiResponse>> {
    return this.makeRequest<DataApiResponse>('/data/update', {
      method: 'PUT',
      body: { data },
      requiresAuth: true
    });
  }

  async getDataVersions(limit?: number): Promise<ApiResponse<DataApiResponse[]>> {
    const endpoint = limit ? `/data/versions?limit=${limit}` : '/data/versions';
    return this.makeRequest<DataApiResponse[]>(endpoint, {
      requiresAuth: true
    });
  }

  // Health check endpoint
  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: number }>> {
    return this.makeRequest('/health');
  }

  // Utility methods
  createSuccessResponse<T>(data: T): ApiResponse<T> {
    return {
      success: true,
      data,
      timestamp: Date.now()
    };
  }

  createErrorResponse(error: string | ServiceError): ApiResponse {
    const errorMessage = typeof error === 'string' ? error : error.message;
    return {
      success: false,
      error: errorMessage,
      timestamp: Date.now()
    };
  }

  // Check if we're in development mode
  isDevelopment(): boolean {
    return import.meta.env.DEV || import.meta.env.REACT_APP_ENV === 'development';
  }
}